import { CalendarCheck } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * Panorama de punta a punta: cada paso enlaza con el módulo que lo detalla
 * (Calendario, Pedidos, Producción). No repite botones ya documentados en
 * esos módulos; solo ordena el flujo diario completo para que cualquier
 * usuario entienda dónde entra su propio trabajo.
 */
export const DAY_FLOW_MODULE: GuideModule = {
  id: 'day-flow',
  title: 'Cómo funciona un día de trabajo',
  icon: CalendarCheck,
  requiredPermission: null,
  priority: 1,
  summary:
    'Así se conectan los módulos durante un día normal de trabajo: primero se planifica y publica el menú, después las empresas (o el equipo interno) cargan sus pedidos antes de la hora de corte, el Tablero del día muestra quién ya pidió y quién falta, y "Cerrar corte" confirma todo lo enviado para esa fecha y, en el mismo paso, genera el remito de cada pedido confirmado. Con el corte cerrado, Producción arma la planilla y la cierra: esa es la versión que usa cocina. Se imprime el lote de remitos del día para reparto, y a medida que se entregan se carga la firma de recepción de cada uno. Si después del cierre se aprueba un cambio, queda registrado como anexo, sin tocar la planilla original. Periódicamente (no todos los días: según el ciclo de facturación de cada contrato), los remitos firmados de un cliente se agrupan en una factura desde Facturación. Las notificaciones te avisan en cada paso, y las empresas hacen su parte desde el portal del cliente, no desde este sistema.',
  steps: [
    {
      title: 'Planificar y publicar el menú',
      description:
        'Desde Calendario se define qué menú está disponible para cada fecha, con su cupo máximo, y se publica para que las empresas puedan pedirlo. Antes de publicar, es solo un borrador interno.',
      keyTerms: ['entrada-de-calendario', 'publicado', 'cupo'],
    },
    {
      title: 'Las empresas cargan su pedido antes del corte',
      description:
        'Cada empresa carga su pedido desde el portal del cliente (o el equipo interno lo carga por ella) antes de la hora de corte de su contrato. Mientras el pedido esté en borrador o enviado, todavía se puede editar.',
      keyTerms: ['pedido', 'hora-de-corte'],
    },
    {
      title: 'Revisar el Tablero del día',
      description:
        'El Tablero del día muestra, para la fecha elegida, qué empresas ya pidieron, cuáles faltan y si algún pedido está por debajo del tope mínimo o se desvía del promedio habitual.',
      keyTerms: ['tablero-del-dia', 'desvio'],
    },
    {
      title: 'Cerrar el corte',
      description:
        'Con el cierre automático encendido, cada pedido se cierra solo a su hora de corte (solo de hoy en adelante; los días anteriores se cierran a mano). Si lo apagaste, o querés cerrar antes, hacé clic en "Cerrar corte": se confirman los pedidos enviados y se cancelan los que quedaron en borrador. No se puede deshacer.',
      keyTerms: ['cierre-de-corte', 'pedido-confirmado'],
    },
    {
      title: 'Producción arma la planilla y la cierra',
      description:
        'Con el corte cerrado, Producción consolida los pedidos confirmados en una planilla provisoria. Cuando está lista, se cierra con "Cerrar planilla": esa versión congelada es la que usa cocina.',
      keyTerms: ['planilla-de-produccion', 'planilla-cerrada'],
    },
    {
      title: 'Los cambios aprobados después del cierre quedan como anexo',
      description:
        'Si alguien pide un cambio sobre un pedido ya confirmado y se aprueba después de cerrar la planilla, el cambio no modifica la planilla original: se registra aparte, como anexo.',
      keyTerms: ['solicitud-de-cambio', 'anexo'],
    },
    {
      title: 'Imprimir o exportar para cocina',
      description:
        'Desde Producción, imprimís la planilla cerrada o la exportás como CSV para llevarla a cocina o analizarla en una planilla de cálculo.',
      keyTerms: ['exportar-csv'],
    },
    {
      title: 'Imprimir y firmar los remitos de reparto',
      description:
        'Con el corte cerrado, cada pedido confirmado ya tiene su remito generado automáticamente (si a alguno le falta, se puede generar a mano desde "Generar remitos" en Remitos). Desde Remitos, imprimís el lote completo de la fecha en un solo PDF para reparto, y a medida que cada entrega se completa, cargás la firma de recepción escaneada — o, si quién recibe puede firmar en el momento, usás "Firmar en pantalla" (o "Registrar entrega y firma" sobre un remito todavía "Despachado") sin necesidad de ningún archivo.',
      keyTerms: ['remito', 'remito-firmado', 'firma-en-pantalla'],
    },
    {
      title: 'Los remitos firmados eventualmente se facturan',
      description:
        'No es un paso diario: según el ciclo de facturación de cada contrato (semanal, quincenal o mensual), Facturación agrupa los remitos firmados de un cliente en un borrador de factura, lo revisás, y lo emitís para obtener el CAE.',
      keyTerms: ['remito-firmado', 'borrador-de-factura', 'factura'],
    },
  ],
  states: [],
  faq: [
    {
      question: '¿En qué momento del día entran las notificaciones?',
      answer:
        'En varios: cuando confirmás un pedido, cuando se cierra un corte, cuando alguien pide un cambio y cuando se aprueba o rechaza. La campana te avisa de todo esto sin que tengas que estar revisando cada pantalla.',
    },
    {
      question: '¿Las empresas ven este mismo sistema?',
      answer:
        'No. Las empresas cargan y revisan sus pedidos desde el portal del cliente, una dirección separada pensada solo para eso. No ven el Tablero del día, Producción ni ningún módulo interno.',
    },
    {
      question: '¿Qué pasa si una empresa pide después del corte?',
      answer:
        'Pasada la hora de corte, la empresa ya no puede cargar ni editar el pedido de esa fecha. Si el contrato lo permite, alguien del equipo con permiso para exceder el corte puede cargarlo igual, indicando el motivo: el pedido queda marcado como cargado fuera de hora y entra en esa misma fecha. Si ya se cerró el corte, el camino es una solicitud de cambio.',
    },
    {
      question:
        '¿Puedo saltarme un paso, por ejemplo cerrar la planilla sin cerrar el corte primero?',
      answer:
        'No. Producción necesita que el corte de esa fecha ya esté cerrado antes de poder cerrar su propia planilla: es el mismo orden que sigue el flujo diario completo.',
    },
  ],
  tips: [
    'Si sos nuevo en el sistema, seguí este panorama una vez antes de entrar a un módulo específico: te va a ayudar a entender por qué existe cada botón.',
    'Revisá el Tablero del día antes de cerrar el corte: es más fácil completar lo que falta antes que gestionar solicitudes de cambio después.',
    'Un anexo no es un error: es la forma prevista de registrar un cambio aprobado después de que la planilla ya se cerró.',
  ],
};
